export const DEFAULT_REGION = 'johannesburg'
export const DEFAULT_UBER_ACCOUNT = 'uber-go-jhb'

/** Lock the app to Johannesburg UBER EVs (no region/account switching). */
export const setJohannesburgEvsContext = () => {
  if (typeof window === 'undefined') return
  localStorage.setItem('region', DEFAULT_REGION)
  localStorage.setItem('uberAccount', DEFAULT_UBER_ACCOUNT)
}

const LEGACY_TOKEN_KEY = 'token'
const LEGACY_AUTH_TOKEN_KEY = 'auth_token'
const LEGACY_CONTRACTOR_TOKEN_KEY = 'contractor_token'
const AUTH_SCOPE_MARKER_KEY = 'valt_auth_scope'

export const getCurrentRegion = () => {
  if (typeof window === 'undefined') {
    return DEFAULT_REGION
  }
  // Portal login persists `contractor_region`; admin uses `region`.
  return (
    localStorage.getItem('region') ||
    localStorage.getItem('contractor_region') ||
    DEFAULT_REGION
  )
}

/** Region normalized for API: lowercased, spaces → underscore. Use for X-Region header / region query. */
export const getRegionForApi = (): string => {
  const raw = getCurrentRegion()
  if (!raw) return DEFAULT_REGION
  return String(raw).toLowerCase().trim().replace(/\s+/g, '_') || DEFAULT_REGION
}

export const appendRegionToUrl = (url: string) => {
  const separator = url.includes('?') ? '&' : '?'
  return `${url}${separator}region=${getCurrentRegion()}`
}

// Re-export single-schema region scoping for convenience
export {
  isSingleSchemaEnabled,
  regionIdFromCode,
  regionCodeFromId,
  currentRegionId,
  appendRegionScopeToParams,
  regionScopeHeaders,
} from '@/services/regionScope'

/**
 * Read `region` / `connection` from a valt-vehicles JSON envelope or embedded `data` object (no localStorage).
 * Use for compliance and single-vehicle flows so X-Region / ?region= match the API response.
 */
export function parseValtVehicleResponseRegion(json: Record<string, unknown>): string | null {
  const pick = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v.trim() : null)

  const top = pick(json.region ?? json.connection ?? json.Region ?? json.Connection)
  if (top) return top

  const data = json.data
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const d = data as Record<string, unknown>
    const fromData = pick(d.region ?? d.connection ?? d.Region ?? d.Connection)
    if (fromData) return fromData
  }

  const debug = json.debug
  if (debug && typeof debug === 'object' && !Array.isArray(debug)) {
    const fromDebug = pick((debug as Record<string, unknown>).region)
    if (fromDebug) return fromDebug
  }

  return null
}

/** UTEH API `connection` query values (regional DB names). */
export const UTEH_CONNECTION_OPTIONS = ['johannesburg', 'cape_town', 'durban'] as const

export type UtehConnection = (typeof UTEH_CONNECTION_OPTIONS)[number]

/**
 * Map console region (localStorage, including Uber variants like johannesburg_go) to UTEH `connection`.
 */
export const getUtehConnectionFromRegion = (regionOverride?: string): UtehConnection => {
  const raw = regionOverride ?? getRegionForApi()
  if (raw.startsWith('johannesburg')) return 'johannesburg'
  if (raw.startsWith('cape_town')) return 'cape_town'
  if (raw.startsWith('durban')) return 'durban'
  if ((UTEH_CONNECTION_OPTIONS as readonly string[]).includes(raw)) return raw as UtehConnection
  return DEFAULT_REGION as UtehConnection
}

import { isClientPortalHost, isContractorAppPath } from '@/utils/clientPortalHost'

/** Token from logged-in user.
 * - For main console/admin app: uses `token` or `auth_token`.
 * - For client portal (/contractor/..., /clients/login, or clients.* host): prefers `contractor_token` from client login.
 */
export const getAuthToken = (): string | null => {
  if (typeof window === 'undefined') return null
  syncScopedAuthStorage()

  const contractorToken = getContractorToken()
  const isContractorPath = isContractorAppPath(window.location.pathname)

  // In client portal context, prefer contractor token if available
  if ((isClientPortalHost() || isContractorPath) && contractorToken) {
    return contractorToken
  }

  // Fallback to main app tokens
  return (
    localStorage.getItem(SCOPED_TOKEN_KEY) ||
    localStorage.getItem(SCOPED_AUTH_TOKEN_KEY) ||
    localStorage.getItem(LEGACY_TOKEN_KEY) ||
    localStorage.getItem(LEGACY_AUTH_TOKEN_KEY)
  )
}

/** Headers with Bearer token for API requests. Set contentType: false for FormData. */
export const getAuthHeaders = (options?: { contentType?: boolean }): Record<string, string> => {
  const token = getAuthToken()
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (options?.contentType !== false) {
    headers['Content-Type'] = 'application/json'
  }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  return headers
}

/** Trim and strip trailing slashes so `${base}/path` never becomes `.../api//path`. */
export function normalizeApiBaseUrl(base: string): string {
  return base.trim().replace(/\/+$/, '')
}

/** API base from `VITE_API_URL` only — no hardcoded host fallbacks. */
export const VALT_API_BASE = normalizeApiBaseUrl(String(import.meta.env.VITE_API_URL || ''))

/** Rewrite absolute URLs that still point at old API hosts to the current `VALT_API_BASE`. */
const LEGACY_API_BASES = [
  'http://localhost:8080/api',
  'http://13.247.83.135:8080/api',
  'http://13.247.83.135:953/api',
  'https://api-v2.valt.africa/api',
]
const LEGACY_STORAGE_ORIGINS = [
  'http://localhost:8080',
  'http://13.247.83.135:8080',
  'http://13.247.83.135:953',
  'https://api-v2.valt.africa',
]

/** Host origin for static files under `/storage/...` (no trailing slash). */
export function getApiOrigin(): string {
  const apiBase = VALT_API_BASE
  if (/^https?:\/\//i.test(apiBase)) {
    return apiBase.replace(/\/api\/?$/i, '')
  }
  const proxyTarget = import.meta.env.VITE_API_PROXY_TARGET
  if (proxyTarget && /^https?:\/\//i.test(String(proxyTarget).trim())) {
    return normalizeApiBaseUrl(String(proxyTarget))
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin
  }
  return ''
}

/** Resolve `/storage/...`, `client-logos/...`, or legacy-host URLs to the current API origin. */
export function resolveStorageUrl(candidate: string): string {
  const trimmed = candidate.trim()
  if (!trimmed) return ''

  if (/^https?:\/\//i.test(trimmed)) {
    for (const legacyOrigin of LEGACY_STORAGE_ORIGINS) {
      if (trimmed.startsWith(`${legacyOrigin}/`) || trimmed === legacyOrigin) {
        return `${getApiOrigin()}${trimmed.slice(legacyOrigin.length)}`
      }
    }
    return trimmed
  }

  let path = trimmed.startsWith('/') ? trimmed : `/${trimmed}`
  if (!path.startsWith('/storage/')) {
    path = `/storage/${path.replace(/^\//, '')}`
  }
  return `${getApiOrigin()}${path}`
}
const AUTH_SCOPE_RAW = String(
  import.meta.env.VITE_AUTH_SCOPE ||
    import.meta.env.VITE_API_PROXY_TARGET ||
    import.meta.env.VITE_API_URL ||
    VALT_API_BASE,
)
const AUTH_SCOPE =
  AUTH_SCOPE_RAW.toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '') || 'default'
const SCOPED_TOKEN_KEY = `${LEGACY_TOKEN_KEY}_${AUTH_SCOPE}`
const SCOPED_AUTH_TOKEN_KEY = `${LEGACY_AUTH_TOKEN_KEY}_${AUTH_SCOPE}`
const SCOPED_CONTRACTOR_TOKEN_KEY = `${LEGACY_CONTRACTOR_TOKEN_KEY}_${AUTH_SCOPE}`

function syncScopedAuthStorage(): void {
  if (typeof window === 'undefined') return
  const activeScope = localStorage.getItem(AUTH_SCOPE_MARKER_KEY)
  if (activeScope !== AUTH_SCOPE) {
    localStorage.removeItem(LEGACY_TOKEN_KEY)
    localStorage.removeItem(LEGACY_AUTH_TOKEN_KEY)
    localStorage.removeItem(LEGACY_CONTRACTOR_TOKEN_KEY)
    localStorage.setItem(AUTH_SCOPE_MARKER_KEY, AUTH_SCOPE)
  }

  const scopedToken = localStorage.getItem(SCOPED_TOKEN_KEY) || localStorage.getItem(SCOPED_AUTH_TOKEN_KEY)
  if (scopedToken && !localStorage.getItem(LEGACY_TOKEN_KEY)) {
    localStorage.setItem(LEGACY_TOKEN_KEY, scopedToken)
  }
}

export const setAuthToken = (token: string): void => {
  if (typeof window === 'undefined') return
  syncScopedAuthStorage()
  localStorage.setItem(SCOPED_TOKEN_KEY, token)
  localStorage.setItem(LEGACY_TOKEN_KEY, token)
}

export const clearAuthToken = (): void => {
  if (typeof window === 'undefined') return
  localStorage.removeItem(SCOPED_TOKEN_KEY)
  localStorage.removeItem(SCOPED_AUTH_TOKEN_KEY)
  localStorage.removeItem(LEGACY_TOKEN_KEY)
  localStorage.removeItem(LEGACY_AUTH_TOKEN_KEY)
}

export const getContractorToken = (): string | null => {
  if (typeof window === 'undefined') return null
  syncScopedAuthStorage()
  return localStorage.getItem(SCOPED_CONTRACTOR_TOKEN_KEY) || localStorage.getItem(LEGACY_CONTRACTOR_TOKEN_KEY)
}

export const setContractorToken = (token: string): void => {
  if (typeof window === 'undefined') return
  syncScopedAuthStorage()
  localStorage.setItem(SCOPED_CONTRACTOR_TOKEN_KEY, token)
  localStorage.setItem(LEGACY_CONTRACTOR_TOKEN_KEY, token)
}

export const clearContractorToken = (): void => {
  if (typeof window === 'undefined') return
  localStorage.removeItem(SCOPED_CONTRACTOR_TOKEN_KEY)
  localStorage.removeItem(LEGACY_CONTRACTOR_TOKEN_KEY)
}
syncScopedAuthStorage()

export const buildApiUrl = (path: string) => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  return `${VALT_API_BASE}${normalizedPath}`
}

/** Build an API URL with query params; supports relative `/api` bases used by the Vite dev proxy. */
export const buildApiUrlWithQuery = (
  path: string,
  query?: Record<string, string | number | undefined | null>,
): string => {
  let href = buildApiUrl(path)
  if (!query) return href

  const params = new URLSearchParams()
  Object.entries(query).forEach(([key, value]) => {
    if (value != null && value !== '') params.set(key, String(value))
  })
  const qs = params.toString()
  if (qs) href += `${href.includes('?') ? '&' : '?'}${qs}`
  return href
}

type JwtPayload = {
  exp?: number
}

function decodeJwtPayload(token: string): JwtPayload | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3 || !parts[1]) {
      return null
    }
    const payload = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))
    return JSON.parse(payload) as JwtPayload
  } catch {
    return null
  }
}

export const hasUsableAuthToken = (token: string | null | undefined): boolean => {
  if (!token || typeof token !== 'string') {
    return false
  }

  const trimmed = token.trim()
  if (!trimmed) {
    return false
  }

  const payload = decodeJwtPayload(trimmed)
  // Opaque / non-JWT tokens cannot be expired client-side; 401 handling covers those.
  if (!payload) {
    return true
  }

  if (typeof payload.exp === 'number') {
    const nowInSeconds = Math.floor(Date.now() / 1000)
    if (payload.exp <= nowInSeconds) {
      return false
    }
  }

  return true
}

const PUBLIC_AUTH_ROUTE_NAMES = new Set([
  'contractor-login',
  'contractor-recover-password',
  'contractor-recover-password-email',
])

let handlingExpiredSession = false

function unauthenticatedResponse(): Response {
  return new Response(JSON.stringify({ message: 'Unauthenticated.', error: 'Token expired' }), {
    status: 401,
    headers: { 'Content-Type': 'application/json' },
  })
}

/** Clear portal session and send the user to login. Safe to call concurrently. */
export function expireContractorAuthSession(): void {
  if (typeof window === 'undefined' || handlingExpiredSession) return
  handlingExpiredSession = true

  void (async () => {
    try {
      const { useContractorAuthStore } = await import('@/stores/contractorAuthStore')
      useContractorAuthStore().logout()
    } catch {
      clearContractorToken()
    }

    try {
      const { default: router } = await import('@/router/portal')
      const routeName = router.currentRoute.value.name
      if (!PUBLIC_AUTH_ROUTE_NAMES.has(String(routeName ?? ''))) {
        await router.replace({ name: 'contractor-login' })
      }
    } catch {
      window.location.assign('/clients/login')
    }
  }).finally(() => {
    handlingExpiredSession = false
  })
}

const normalizeApiRequestInput = (input: RequestInfo | URL): RequestInfo | URL => {
  if (typeof input !== 'string') return input

  for (const legacyBase of LEGACY_API_BASES) {
    if (input.startsWith(legacyBase)) {
      return `${VALT_API_BASE}${input.slice(legacyBase.length)}`
    }
    if (input.startsWith(`${legacyBase}/`)) {
      return `${VALT_API_BASE}${input.slice(legacyBase.length)}`
    }
  }

  return input
}

function isFormDataBody(body: RequestInit['body']): boolean {
  return typeof FormData !== 'undefined' && body instanceof FormData
}

export type ValtFetchInit = RequestInit & { skipAuth?: boolean; timeoutMs?: number }

/**
 * Authenticated fetch to the configured API base URL (VITE_API_URL) with merged headers (Bearer from getAuthToken()).
 * Use skipAuth for public routes (e.g. login, forgot-password) so a stale token is not sent.
 * Caller headers override defaults where set; Authorization from the caller wins if provided.
 * `timeoutMs` aborts the request after that many milliseconds (only applied when `signal` is not set).
 */
export async function valtFetch(input: RequestInfo | URL, init: ValtFetchInit = {}): Promise<Response> {
  const { skipAuth, timeoutMs, ...rest } = init

  if (!skipAuth) {
    const token = getAuthToken()
    if (token && !hasUsableAuthToken(token)) {
      expireContractorAuthSession()
      return unauthenticatedResponse()
    }
  }

  const headers = new Headers()
  if (skipAuth) {
    headers.set('Accept', 'application/json')
    if (!isFormDataBody(rest.body)) {
      headers.set('Content-Type', 'application/json')
    }
  } else {
    const authOpts = isFormDataBody(rest.body) ? { contentType: false as const } : undefined
    const base = getAuthHeaders(authOpts)
    Object.entries(base).forEach(([k, v]) => headers.set(k, v))
  }
  if (rest.headers) {
    new Headers(rest.headers).forEach((value, key) => headers.set(key, value))
  }

  const fetchInit: RequestInit = { ...rest, headers }
  if (timeoutMs && timeoutMs > 0 && !rest.signal) {
    const AT = AbortSignal as typeof AbortSignal & { timeout?: (ms: number) => AbortSignal }
    if (typeof AT.timeout === 'function') {
      fetchInit.signal = AT.timeout(timeoutMs)
    } else {
      const controller = new AbortController()
      setTimeout(() => controller.abort(), timeoutMs)
      fetchInit.signal = controller.signal
    }
  }

  const response = await fetch(normalizeApiRequestInput(input), fetchInit)
  if (!skipAuth && response.status === 401) {
    expireContractorAuthSession()
  }
  return response
}
